import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { message } from 'antd';
import Cookies from 'js-cookie';

import { verify2FA } from 'features/verify2FA/actions';
import {
	getUserProfile,
	getUserProfit,
	getUserProfitSecondaryMarket,
	saveRiskTolerance,
	acceptAgreement,
} from './actions';
import { register, saveContactInfo } from '../registration/actions';
import { login2FA, login, logout } from '../authorization/actions';
import { deleteAccountConfirm } from '../userSettings/actions';
import { deleteCounteroffer } from '../secondaryMarket/secondaryMarketSlice';

import { IUser, IAreaChartItem, RequestStatusTypes } from 'models';

import { apiReducerBuilder } from 'utils';
import i18n from 'utils/i18n';
import { ACCESS_TOKEN, REFRESH_TOKEN, ClientStatus } from 'utils/constants';
import {getActiveSessions} from "./actions/getActiveSessions";
import {removeActiveSessions} from "./actions/removeActiveSessions";
import {removeActiveSession} from "./actions/removeActiveSession";
import {loginOTP} from "../authorization/actions/loginOTP";

interface UserState {
	profit: IAreaChartItem[];
	entity: IUser;
	status: RequestStatusTypes;
	timeUserLoaded: number;
	activeSessions: any[];
	submitting: string;
}

const userSlice = createSlice({
	name: 'user',
	initialState: {
		entity: {},
		profit: [] as IAreaChartItem[],
		activeSessions: [] as any[],
		submitting: '',
	} as UserState,
	reducers: {},
	extraReducers: (builder) => {

		builder.addCase(removeActiveSession.rejected, (state, action) => {
			const { meta } = action;
			state.submitting = meta.requestStatus;
			message.error(i18n.t('errorSessionTermination') as string);
		});
		builder.addCase(removeActiveSession.pending, (state, action) => {
			const { meta } = action;
			state.submitting = meta.requestStatus;
		});
		builder.addCase(removeActiveSession.fulfilled, (state, action) => {
			const { meta } = action;
			state.submitting = meta.requestStatus;
			message.success(i18n.t('sessionHasBeenTerminated') as string);
		});

		builder.addCase(removeActiveSessions.rejected, (state, action) => {
			const { meta } = action;
			state.submitting = meta.requestStatus;
			message.error(i18n.t('errorSessionTermination') as string);
		});
		builder.addCase(removeActiveSessions.pending, (state, action) => {
			const { meta } = action;
			state.submitting = meta.requestStatus;
		});
		builder.addCase(removeActiveSessions.fulfilled, (state, action) => {
			const { meta } = action;
			state.submitting = meta.requestStatus;
			message.success(i18n.t('allSessionsHasBeenTerminated') as string);
		});

		builder.addCase(getActiveSessions.rejected, (state, action) => {
			const { meta } = action;
			state.submitting = meta.requestStatus;
		});
		builder.addCase(getActiveSessions.pending, (state, action) => {
			const { meta } = action;
			state.submitting = meta.requestStatus;
		});
		builder.addCase(getActiveSessions.fulfilled, (state, action) => {
			const { payload, meta } = action;
			state.submitting = meta.requestStatus;
			state.activeSessions = payload;
		});

		apiReducerBuilder(builder, getUserProfile, (state, action) => {
			const { payload, meta } = action;

			const isDeleted = payload.status === ClientStatus.DELETED;

			if (isDeleted) {
				Cookies.remove(ACCESS_TOKEN);
				Cookies.remove(REFRESH_TOKEN);
			}
			state.status = meta.requestStatus;
			state.entity = isDeleted ? {} : payload;
			state.timeUserLoaded = new Date().getTime();
		});

		apiReducerBuilder(builder, saveRiskTolerance, (state, action) => {
			const { payload, meta } = action;
			state.status = meta.requestStatus;
			state.entity = payload;
			state.timeUserLoaded = new Date().getTime();

			message.success(i18n.t('messageResultSavedText') as string);
		});
		apiReducerBuilder(builder, acceptAgreement, (state, action) => {
			const { payload, meta } = action;

			state.status = meta.requestStatus;
			state.entity = payload;
			state.timeUserLoaded = new Date().getTime();
		});

		apiReducerBuilder(builder, getUserProfit, (state, action) => {
			const { payload, meta } = action;
			state.status = meta.requestStatus;
			state.profit = payload;
		});

		apiReducerBuilder(builder, getUserProfitSecondaryMarket, (state, action) => {
			const { payload, meta } = action;
			state.status = meta.requestStatus;
			state.profit = payload;
		});

		builder.addCase(deleteCounteroffer, (state) => {
			state.entity.secondaryMarket.counterOffers.count =
				state.entity.secondaryMarket.counterOffers.count - 1;
		});

		builder.addMatcher(isAnyOf(logout, deleteAccountConfirm.fulfilled), (state) => {
			state.entity = {} as IUser;
			state.profit = [];
		});

		builder.addMatcher(isAnyOf(saveContactInfo.fulfilled, register.fulfilled), (state, action) => {
			const { payload } = action;

			state.entity = { ...state.entity, ...payload };
		});

		builder.addMatcher(isAnyOf(verify2FA.fulfilled), (state, action) => {
			const { payload } = action;

			state.entity = { ...state.entity, tfaActive: payload.tfa_is_active };
		});
		builder.addMatcher(
			isAnyOf(login.fulfilled, login2FA.fulfilled, loginOTP.fulfilled),
			(state, action) => {
			const { payload } = action;

			state.entity = { ...state.entity, status: payload.status };
		});
	},
});

export default userSlice.reducer;
